// Vzhled aplikace: světlý/tmavý režim + barevný motiv (základ a akcent).
// Vše se pamatuje v localStorage prohlížeče, takže volba je per zařízení.
const KEY = 'studio-os-theme';
const BASE_KEY = 'studio-os-base';
const ACCENT_KEY = 'studio-os-accent';

// --- Světlý / tmavý režim ---

export function initTheme(): void {
  const saved = localStorage.getItem(KEY);
  const dark = saved
    ? saved === 'dark'
    : window.matchMedia('(prefers-color-scheme: dark)').matches;
  document.documentElement.classList.toggle('dark', dark);
  initColors();
}

export function toggleTheme(): boolean {
  const el = document.documentElement;
  const dark = !el.classList.contains('dark');
  el.classList.add('theme-switching');
  el.classList.toggle('dark', dark);
  localStorage.setItem(KEY, dark ? 'dark' : 'light');
  void el.offsetHeight;
  window.setTimeout(() => el.classList.remove('theme-switching'), 60);
  return dark;
}

export function isDark(): boolean {
  return document.documentElement.classList.contains('dark');
}

// --- Barevný motiv ---

export type ThemeBase = 'studeny' | 'teply';
export type ThemeAccent = 'neutral' | 'terakota' | 'petrolejova' | 'moduly';

export const BASE_LABEL: Record<ThemeBase, { label: string; hint: string }> = {
  studeny: { label: 'Studená šedá', hint: 'Neutrální antracit — jako dosud' },
  teply: { label: 'Teplá šedá', hint: 'Nádech do hněda, sedí k dílně a dřevu' },
};

export const ACCENT_LABEL: Record<ThemeAccent, { label: string; hint: string; swatch: string }> = {
  neutral: { label: 'Bez akcentu', hint: 'Monochromatické — původní vzhled', swatch: '#18181b' },
  terakota: { label: 'Terakota', hint: 'Pálená cihla — řemeslo a teplo', swatch: '#b4522e' },
  petrolejova: { label: 'Petrolejová', hint: 'Ocelová modrozelená — technické, klidné', swatch: '#1f5f5b' },
  moduly: { label: 'Barva podle modulu', hint: 'Každá sekce má svou barvu — snazší orientace', swatch: 'linear-gradient(90deg,#2563eb,#7c3aed,#059669,#b4522e)' },
};

const isBase = (v: string | null): v is ThemeBase => v === 'studeny' || v === 'teply';
const isAccent = (v: string | null): v is ThemeAccent =>
  v === 'neutral' || v === 'terakota' || v === 'petrolejova' || v === 'moduly';

export function getBase(): ThemeBase {
  const v = localStorage.getItem(BASE_KEY);
  return isBase(v) ? v : 'studeny';
}

export function getAccent(): ThemeAccent {
  const v = localStorage.getItem(ACCENT_KEY);
  return isAccent(v) ? v : 'neutral';
}

// Zapíše volbu na <html> jako data atributy — na ně jsou navázané CSS proměnné.
// Přechody (transition-all) na okamžik vypneme: prohlížeč jinak u barev
// odvozených z CSS proměnných nechá viset předchozí hodnotu, dokud něco
// nevynutí překreslení — tlačítka by po přepnutí zůstala ve staré barvě.
function apply(base: ThemeBase, accent: ThemeAccent): void {
  const el = document.documentElement;
  el.classList.add('theme-switching');
  el.setAttribute('data-base', base);
  el.setAttribute('data-accent', accent);
  void el.offsetHeight; // vynutí přepočet stylů
  window.setTimeout(() => el.classList.remove('theme-switching'), 60);
}

export function initColors(): void {
  apply(getBase(), getAccent());
}

export function setBase(base: ThemeBase): void {
  localStorage.setItem(BASE_KEY, base);
  apply(base, getAccent());
}

export function setAccent(accent: ThemeAccent): void {
  localStorage.setItem(ACCENT_KEY, accent);
  apply(getBase(), accent);
}
